import { colorChannel } from '@mui/system/colorManipulator';

import constants from '../../designTokens/build/constants';

import { Tokens } from './tokens';

import type { ColorScheme } from './types';
import type { ColorSystemOptions } from '@mui/material/styles';

const mapTokenColorToMui = (
  colorObj: Tokens['palette'][
    | 'info'
    | 'error'
    | 'primary'
    | 'secondary'
    | 'success'
    | 'warning'],
  mode: 'light' | 'dark'
) => {
  return {
    1: colorObj['1'][mode],
    2: colorObj['2'][mode],
    3: colorObj['3'][mode],
    4: colorObj['4'][mode],
    5: colorObj['5'][mode],
    6: colorObj['6'][mode],
    7: colorObj['7'][mode],
    8: colorObj['8'][mode],
    9: colorObj['9'][mode],
    10: colorObj['10'][mode],
    11: colorObj['11'][mode],
    12: colorObj['12'][mode],
    a1: colorObj['a1'][mode],
    a2: colorObj['a2'][mode],
    a3: colorObj['a3'][mode],
    a4: colorObj['a4'][mode],
    a5: colorObj['a5'][mode],
    a6: colorObj['a6'][mode],
    a7: colorObj['a7'][mode],
    a8: colorObj['a8'][mode],
    a9: colorObj['a9'][mode],
    a10: colorObj['a10'][mode],
    a11: colorObj['a11'][mode],
    a12: colorObj['a12'][mode],

    50: colorObj['50'][mode],
    100: colorObj['100'][mode],
    200: colorObj['200'][mode],
    300: colorObj['300'][mode],
    400: colorObj['400'][mode],
    500: colorObj['500'][mode],
    600: colorObj['600'][mode],
    700: colorObj['700'][mode],
    800: colorObj['800'][mode],
    900: colorObj['900'][mode],
    950: colorObj['950'][mode],

    '50Channel': colorChannel(colorObj['50'][mode]),
    '100Channel': colorChannel(colorObj['100'][mode]),
    '200Channel': colorChannel(colorObj['200'][mode]),
    '300Channel': colorChannel(colorObj['300'][mode]),
    '400Channel': colorChannel(colorObj['400'][mode]),
    '500Channel': colorChannel(colorObj['500'][mode]),
    '600Channel': colorChannel(colorObj['600'][mode]),
    '700Channel': colorChannel(colorObj['700'][mode]),
    '800Channel': colorChannel(colorObj['800'][mode]),
    '900Channel': colorChannel(colorObj['900'][mode]),
    '950Channel': colorChannel(colorObj['950'][mode]),

    lightest: colorObj['lightest'][mode],
    light: colorObj['light'][mode],
    main: colorObj['main'][mode],
    dark: colorObj['dark'][mode],
    darkest: colorObj['darkest'][mode],

    lightestChannel: colorChannel(colorObj['lightest'][mode]),
    lightChannel: colorChannel(colorObj['light'][mode]),
    mainChannel: colorChannel(colorObj['main'][mode]),
    darkChannel: colorChannel(colorObj['dark'][mode]),
    darkestChannel: colorChannel(colorObj['darkest'][mode]),

    og: colorObj['main'][mode],
    ogSmart: colorObj['main'][mode],

    backgroundSubtlest: colorObj['backgroundSubtlest'][mode],
    backgroundSubtle: colorObj['backgroundSubtle'][mode],
    backgroundSoft: colorObj['backgroundSoft'][mode],
    backgroundSoftHover: colorObj['backgroundSoftHover'][mode],
    borderRegular: colorObj['borderRegular'][mode],
    borderStrong: colorObj['borderStrong'][mode],
    solid: colorObj['solid'][mode],
    solidHover: colorObj['solidHover'][mode],
    text: colorObj['text'][mode],
    contrastText: colorObj['contrast'][mode] || '#FFF',

    activated: `rgba(${colorChannel(colorObj['main'][mode])} / ${
      constants.palette.action['activatedOpacity'][mode]
    })`,
    hovered: `rgba(${colorChannel(colorObj['main'][mode])} / ${
      constants.palette.action['hoverOpacity'][mode]
    })`,
    selected: `rgba(${colorChannel(colorObj['main'][mode])} / ${
      constants.palette.action['selectedOpacity'][mode]
    })`,
  };
};

const generatePaletteForMode = (palette, mode: 'light' | 'dark') => {
  return {
    action: {
      active: palette.action['active'][mode],
      hover: palette.action['hover'][mode],
      hoverOpacity: palette.action['hoverOpacity'][mode],
      selected: palette.action['selected'][mode],
      selectedOpacity: palette.action['selectedOpacity'][mode],
      disabled: palette.action['disabled'][mode],
      disabledOpacity: palette.action['disabledOpacity'][mode],
      focus: palette.action['focus'][mode],
      focusOpacity: palette.action['focusOpacity'][mode],
      activated: palette.action['activated'][mode],
      activatedOpacity: palette.action['activatedOpacity'][mode],
      disabledBackground: palette.action['disabledBackground'][mode],
    },
    background: {
      default: palette.background.primary[mode],
      defaultChannel: colorChannel(palette.background.primary[mode]),
      paper: palette.background.paper[mode],
      paperChannel: colorChannel(palette.background.paper[mode]),
      level1: palette.background['level1'][mode],
      level1Channel: colorChannel(palette.background['level1'][mode]),
      level2: palette.background['level2'][mode],
      level2Channel: colorChannel(palette.background['level2'][mode]),
      level3: palette.background['level3'][mode],
      level3Channel: colorChannel(palette.background['level3'][mode]),
    },
    common: {
      black: palette.black[mode],
      white: palette.white[mode],
    },
    divider: palette.divider['regular'][mode],
    error: mapTokenColorToMui(palette.error, mode),
    info: mapTokenColorToMui(palette.info, mode),
    primary: mapTokenColorToMui(palette.primary, mode),
    secondary: mapTokenColorToMui(palette.secondary, mode),
    success: mapTokenColorToMui(palette.success, mode),
    warning: mapTokenColorToMui(palette.warning, mode),
    alert: mapTokenColorToMui(palette.primary, mode),
    neutral: mapTokenColorToMui(palette.secondary, mode),
    text: {
      primary: palette.text.primary[mode],
      secondary: palette.text.secondary[mode],
      tertiary: palette.text.tertiary[mode],
      disabled: palette.text.disabled[mode],
    },

    Avatar: { defaultBg: palette.avatar.background[mode] },
    Backdrop: { bg: 'rgba(0, 0, 0, 0.5)' },
    OutlinedInput: {
      border: 'var(--mui-palette-divider)',
    },
    TableCell: { border: 'var(--mui-palette-divider)' },
    Tooltip: { bg: 'rgba(10, 13, 20, 0.75)' },
  };
};

export function colorSchemes(
  colors: Tokens['palette']
): Partial<Record<ColorScheme, ColorSystemOptions>> {
  try {
    const colorSchemes = {
      dark: {
        palette: generatePaletteForMode(colors, 'dark'),
      },
      light: {
        palette: generatePaletteForMode(colors, 'light'),
      },
    };
    return colorSchemes;
  } catch (e) {
    const message =
      'Error generating color schemes - This may be due to outdated tokens. If you have recently pulled changes please run  `npm run tokens`';
    throw new Error(message, { cause: e });
  }
}
